import type * as zod from 'zod';

import * as log from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepFailure } from '@sb/routine-runner/types';
import {
  TeleopStateStatusEnum,
  type TeleopState,
} from '@sb/sb-robot-sdk/src/codegen/typescript-axios';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { EventEmitter } from '@sb/utilities';

import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables, { type TeleopExitReason } from './Variables';

/** Polling interval for teleop state monitoring (ms) */
const TELEOP_POLL_INTERVAL_MS = 250;

const ns = log.namespace('TeleopStep');

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

export default class TeleopStep extends Step<Arguments, Variables> {
  private events = new EventEmitter<{
    stop: void;
    complete: { exitReason: TeleopExitReason };
    failure: StepFailure;
  }>();

  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  /**
   * Timeout handle for automatic step termination
   * @internal
   */
  private timeoutHandle: NodeJS.Timeout | null = null;

  /**
   * Cleanup function for teleop state monitoring
   * @internal
   */
  private stopTeleopMonitoring: (() => void) | null = null;

  /**
   * Tracks whether teleop session was ever active during this step
   * @internal
   */
  private teleopWasActive = false;

  private pauseReason: string | undefined;

  public initializeVariableState(): void {
    this.variables = {
      status: 'waiting',
      exitReason: undefined,
    };
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    this.teleopWasActive = false;

    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: 'Teleop step not supported in visualizer',
      });
    }

    // Require CSM feature flag
    if (!getFeatureFlagSync('useControlSourceManager', false)) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason:
          'Teleop step requires the Control Source Manager feature flag to be enabled',
      });
    }

    // Check for connected teleop clients before starting
    const { sdk } = this.routineContext;

    if (sdk?.teleop?.getState) {
      try {
        const teleopState = await sdk.teleop.getState();
        const connectedClients = teleopState.data.connectedClients ?? [];

        if (connectedClients.length === 0) {
          return fail({
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: 'Teleop',
            },
            failureReason:
              'No teleop client connected. Please open the WebXR page on an AR headset before running this step.',
          });
        }

        log.info(ns`play`, 'Teleop client connected', {
          clientCount: connectedClients.length,
          clients: connectedClients.map(
            (c: { clientType?: string }) => c.clientType,
          ),
        });
      } catch (err) {
        log.warn(ns`play`, 'Failed to check teleop client status', { err });
        // Continue anyway - the monitoring will catch errors
      }
    }

    try {
      log.info(ns`play`, 'Teleop step started, waiting for operator', {
        timeoutSeconds: this.args.timeoutSeconds,
      });

      this.setVariable('status', 'waiting');

      // Set up timeout
      this.setupTimeout();

      // Start monitoring teleop state
      this.startTeleopMonitoring();

      // Wait for completion or failure
      const outcome = await Promise.race([
        this.events.next('complete'),
        this.events.next('failure').then((failure) => Promise.reject(failure)),
      ]);

      // Set final state
      this.setVariable('status', 'completed');
      this.setVariable('exitReason', outcome.exitReason);

      log.info(ns`play`, 'Teleop step completed', {
        exitReason: outcome.exitReason,
      });
    } catch (error) {
      // Handle failure case - this stops the routine
      if ((error as StepFailure).failure) {
        // This is a StepFailure from events (Timeout, Interrupted, etc.)
        this.setVariable('status', 'failed');

        return fail(error as StepFailure);
      }

      // Unexpected error - fail the routine
      this.setVariable('status', 'failed');
      this.setVariable('exitReason', 'Error');

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: `Teleop step failed: ${(error as Error)?.message}`,
        error: error as Error,
      });
    } finally {
      // Stop teleop monitoring
      if (this.stopTeleopMonitoring) {
        this.stopTeleopMonitoring();
        this.stopTeleopMonitoring = null;
      }

      // Clear timeout
      if (this.timeoutHandle) {
        clearTimeout(this.timeoutHandle);
        this.timeoutHandle = null;
      }
    }
  }

  public async _pause(args: StepAction): Promise<void> {
    log.warn(
      ns`pause`,
      'Pause on "teleop" step is not supported, stopping step.',
    );

    this.pauseReason = args.reason;
    await this._stop();
  }

  public async _stop(): Promise<void> {
    // Stop teleop monitoring
    if (this.stopTeleopMonitoring) {
      this.stopTeleopMonitoring();
      this.stopTeleopMonitoring = null;
    }

    // Clear timeout
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }

    // Set exit reason if not already set (e.g., from timeout or completion)
    if (!this.variables.exitReason) {
      this.setVariable('exitReason', 'Interrupted');
      this.setVariable('status', 'failed');

      const failureMessage =
        this.pauseReason || // Show pause reason like collision or IK if available
        this.getFailureMessage(
          'Teleop step was interrupted with a pause', // fallback default message
        );

      // Emit failure to unblock Promise.race in _play()
      this.events.emit('failure', {
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: failureMessage,
      });
    }

    log.info(ns`stop`, 'Teleop step stopped');
  }

  public hasSelfTimeout(): boolean {
    // We handle our own timeout
    return false;
  }

  /**
   * Set up the step timeout, fail the step if the teleop session did not complete within the timeout.
   */
  private setupTimeout(): void {
    this.timeoutHandle = setTimeout(() => {
      log.warn(ns`timeout`, 'Teleop step timeout reached', {
        timeoutSeconds: this.args.timeoutSeconds,
      });

      this.setVariable('exitReason', 'Timeout');

      // Emit failure with empty failureReason to stop routine without persistent error message
      this.events.emit('failure', {
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: '',
      });
    }, this.args.timeoutSeconds * 1000);
  }

  /**
   * Start monitoring teleop state to detect when operator calls /teleop/start and /teleop/stop.
   *
   * The teleop client (WebXR/AR headset) controls the teleop session via public API:
   * 1. Operator calls /teleop/start → status transitions to TELEOP
   * 2. Operator calls /teleop/stop → status transitions to STOPPED
   *
   * Exit reasons:
   * - 'Completed': Teleop started and stopped successfully → routine continues
   * - 'Timeout': Step timeout reached before teleop completed → routine fails
   * - 'Error': Teleop error occurred (primary or secondary bot) → routine fails
   * - 'Interrupted': Step was stopped externally → routine fails
   */
  private startTeleopMonitoring(): void {
    const { sdk } = this.routineContext;

    if (!sdk) {
      const errorMessage =
        'Public API SDK is not available, failing teleop step';

      log.error(ns`monitoring`, errorMessage);

      this.setVariable('exitReason', 'Error');

      this.events.emit('failure', {
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: errorMessage,
      });

      return;
    }

    this.stopTeleopMonitoring = this.startMonitoring(
      async (): Promise<{ triggered: boolean; context?: TeleopState }> => {
        // Use public api to get aggregated state including secondary bots
        const response = await sdk.teleop.getState();
        const state = response.data;

        // Check for errors on secondary bots
        const secondaryErrors = this.getSecondaryBotErrors(state);

        if (secondaryErrors.length > 0) {
          log.error(ns`monitoring`, 'Secondary bot error detected', {
            errors: secondaryErrors,
          });

          return {
            triggered: true,
            context: {
              ...state,
              status: TeleopStateStatusEnum.Error,
              errors: secondaryErrors,
            },
          };
        }

        // Track teleop state transitions based on operator's API calls
        switch (state.status) {
          case TeleopStateStatusEnum.Teleop:
            // Operator called /teleop/start - teleop session is active
            if (!this.teleopWasActive) {
              this.teleopWasActive = true;
              this.setVariable('status', 'active');

              log.info(ns`monitoring`, 'Operator started teleop session');

              // Add 'teleop' tag to recording when teleop starts
              await this.conditionallyAddRecordingTag('teleop');
            }

            return { triggered: false };

          case TeleopStateStatusEnum.Stopped:
            // Operator called /teleop/stop - check if this was a completed exit
            if (this.teleopWasActive) {
              log.info(ns`monitoring`, 'Operator stopped teleop session');

              return { triggered: true, context: state };
            }

            // Still waiting for operator to start teleop
            return { triggered: false };

          case TeleopStateStatusEnum.WaitForTeleop:
            // Still waiting for operator to call /teleop/start
            return { triggered: false };

          case TeleopStateStatusEnum.Error:
            // Teleop error occurred - this will fail the routine
            log.error(ns`monitoring`, 'Teleop error detected', {
              errors: state.errors,
            });

            // Add 'teleop-error' tag to recording when teleop error occurs
            await this.conditionallyAddRecordingTag('teleop-error');

            return { triggered: true, context: state };

          case TeleopStateStatusEnum.Stopping:
            // Teleop is stopping, wait for it to finish
            return { triggered: false };

          default:
            return { triggered: false };
        }
      },
      {
        onTriggered: (_callbacks, context?: TeleopState) => {
          if (!context) {
            return;
          }

          if (context.status === TeleopStateStatusEnum.Error) {
            // Teleop error - fail the routine
            this.setVariable('exitReason', 'Error');

            const fallbackMessage = `Teleop error: ${context.errors?.join(', ') || 'Unknown error'}`;

            this.events.emit('failure', {
              failure: {
                kind: FailureKind.StepPlayFailure,
                stepKind: 'Teleop',
              },
              failureReason: this.getFailureMessage(fallbackMessage),
            });
          } else if (context.status === TeleopStateStatusEnum.Stopped) {
            // Completed exit - routine continues
            this.events.emit('complete', { exitReason: 'Completed' });
          }
        },
        onError: (err) => {
          log.error(ns`monitoring`, 'Error polling teleop state', { err });

          // Monitoring error - fail the routine
          this.setVariable('exitReason', 'Error');

          const fallbackMessage = `Failed to monitor teleop state: ${(err as Error)?.message}`;

          this.events.emit('failure', {
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: 'Teleop',
            },
            failureReason: this.getFailureMessage(fallbackMessage),
            error: err as Error,
          });
        },
      },
      { pollIntervalMs: TELEOP_POLL_INTERVAL_MS },
    );
  }

  /**
   * Get the failure message, preferring ControlSystemEvent and collision messages if available.
   * This step streams poses without using doMotion, so it doesn't receive
   * a MotionResult with error details. Instead, we check the robot's failure reason directly.
   * @param fallbackMessage - Message to use if no robot failure reason is available
   * @returns The failure message to display to the user
   */
  private getFailureMessage(fallbackMessage: string): string {
    const robotFailureReason = this.routineContext.robot.getFailureReason();

    if (robotFailureReason?.kind === 'controlSystemEvent') {
      return robotFailureReason.controlSystemEvent.message;
    }

    if (robotFailureReason?.kind === 'collision') {
      return `Joint ${robotFailureReason.collision.joint} encountered a collision`;
    }

    return fallbackMessage;
  }

  /**
   * Extract errors from secondary bots in the teleop state.
   * Returns an array of error messages from secondary bots that are in error state.
   */
  private getSecondaryBotErrors(state: TeleopState): string[] {
    const errors: string[] = [];

    if (!state.config?.bots) {
      return errors;
    }

    for (const bot of state.config.bots) {
      if (bot.status === TeleopStateStatusEnum.Error) {
        const botName = bot.alias || bot.botId;

        const botErrors = bot.errors?.length
          ? bot.errors.map((e: string) => `${botName}: ${e}`)
          : [`${botName}: Unknown error`];

        errors.push(...botErrors);
      }
    }

    return errors;
  }
}
